/**
 * 提供商配置草稿：类型、官方基址提示、校验与「草稿 -> 契约配置」转换（T15）。
 *
 * 纯函数模块，不依赖 React / 存储，便于单测覆盖需求点名的几条规则：
 * - baseUrl 合法性校验；
 * - 必填缺失校验；
 * - 切换 kind 不覆盖已填 baseUrl；
 * - `apiKey` 不进 `ProviderConfig`（只留 `apiKeyRef`）。
 */
import { BUILTIN_PROVIDER_KINDS } from '../../core/providers';
import { providerPurpose } from '../../core/providers';
import type { ProviderConfig, ProviderKind, ProviderPurpose } from '../../core/providers';
import { secretRefForId } from './secretStore';
import type { ProviderProtocol } from './vendors';

/** 表单草稿。只有 apiKey 是明文，保存时落到密钥存储而不是 ProviderConfig。 */
export interface ProviderDraft {
  protocol?: ProviderProtocol;
  vendorId?: string;
  logo?: string;
  kind: ProviderKind;
  purpose: ProviderPurpose;
  name: string;
  baseUrl: string;
  apiKey: string;
  defaultModel: string;
  enabled: boolean;
}

/** 逐字段校验错误；为空对象表示通过。 */
export interface ProviderDraftErrors {
  kind?: string;
  purpose?: string;
  name?: string;
  baseUrl?: string;
  logo?: string;
}

/** 各 kind 的官方基址占位提示（只作 placeholder，不覆盖用户输入）。 */
export const KIND_BASE_URL_HINT: Record<ProviderKind, string> = {
  openai: 'https://api.openai.com/v1',
  anthropic: 'https://api.anthropic.com/v1',
  gemini: 'https://generativelanguage.googleapis.com/v1beta',
  custom: 'https://your-gateway.example.com/v1',
};

/** 各 kind 的中文显示名。 */
export const KIND_LABEL: Record<ProviderKind, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
  custom: '自定义',
};

export const PROVIDER_PURPOSE_LABEL: Record<ProviderPurpose, string> = {
  chat: '语言推理模型',
  image: '生图模型',
  tts: '语音合成模型',
};

/** 表单里 kind 的展示顺序。 */
export const PROVIDER_KIND_ORDER: readonly ProviderKind[] = BUILTIN_PROVIDER_KINDS;

export function createProviderDraft(overrides: Partial<ProviderDraft> = {}): ProviderDraft {
  return {
    kind: 'openai',
    purpose: 'chat',
    name: '',
    baseUrl: '',
    apiKey: '',
    defaultModel: '',
    enabled: true,
    ...overrides,
  };
}

/** 切换 kind：只改类型，**不动**用户已填值（含 baseUrl）。 */
export function applyKindChange(draft: ProviderDraft, kind: ProviderKind): ProviderDraft {
  return {
    ...draft,
    kind,
    ...(draft.protocol === undefined ? {} : { protocol: kind === 'anthropic' || kind === 'gemini' ? kind : 'chat-completions' as const }),
    purpose: kind === 'anthropic' || kind === 'gemini' ? 'chat' : draft.purpose,
  };
}

/** 只有 OpenAI 兼容提供商才具备图片生成接口。 */
export function supportsImagePurpose(kind: ProviderKind): boolean {
  return kind === 'openai' || kind === 'custom';
}

export function supportsTtsPurpose(kind: ProviderKind): boolean {
  return kind === 'openai' || kind === 'custom';
}

/** baseUrl 合法性：必须是 http / https 的绝对地址。 */
export function isValidBaseUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return false;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:';
}

/** 表单校验：返回逐字段错误（空对象表示通过）。 */
export function validateProviderDraft(draft: ProviderDraft): ProviderDraftErrors {
  const errors: ProviderDraftErrors = {};
  if (!BUILTIN_PROVIDER_KINDS.includes(draft.kind)) {
    errors.kind = '请选择提供商类型';
  }
  if (draft.purpose === 'image' && !supportsImagePurpose(draft.kind)) errors.purpose = '该提供商类型不支持生图模型';
  if (draft.purpose === 'tts' && !supportsTtsPurpose(draft.kind)) errors.purpose = '该提供商类型不支持语音合成';
  if (draft.protocol === 'responses' && draft.purpose !== 'chat') errors.purpose = 'Responses 协议仅支持语言推理模型';
  if (draft.name.trim() === '') {
    errors.name = '请填写名称';
  }
  if (!isValidBaseUrl(draft.baseUrl)) {
    errors.baseUrl = '请填写合法的 http(s) 基址';
  }
  if (draft.logo && !(/^(https:\/\/[^\s]+|data:image\/(png|jpeg|webp|gif);base64,[A-Za-z0-9+/]+=*)$/.test(draft.logo) && draft.logo.length <= 174828)) {
    errors.logo = '请填写 HTTPS 图片地址或上传图片';
  }
  return errors;
}

export function hasDraftErrors(errors: ProviderDraftErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * 草稿 -> 契约配置。
 * 关键：返回值严格是 `ProviderConfig`，**不含 apiKey**；密钥只以 apiKeyRef 引用。
 */
export function draftToProviderConfig(draft: ProviderDraft, id: string): ProviderConfig {
  const defaultModel = draft.defaultModel.trim();
  return {
    id,
    kind: draft.kind,
    name: draft.name.trim(),
    baseUrl: draft.baseUrl.trim(),
    apiKeyRef: secretRefForId(id),
    enabled: draft.enabled,
    purpose: supportsImagePurpose(draft.kind) ? draft.purpose : 'chat',
    ...(draft.protocol === undefined ? {} : { protocol: draft.protocol }),
    ...(draft.vendorId === undefined ? {} : { vendorId: draft.vendorId }),
    ...(draft.logo === undefined ? {} : { logo: draft.logo }),
    ...(defaultModel === '' ? {} : { defaultModel }),
  };
}

/** 已保存配置 -> 表单草稿（apiKey 由调用方从密钥存储读出后传入）。 */
export function providerConfigToDraft(config: ProviderConfig, apiKey: string): ProviderDraft {
  return {
    kind: config.kind,
    purpose: supportsImagePurpose(config.kind) ? providerPurpose(config) : 'chat',
    name: config.name,
    baseUrl: config.baseUrl,
    apiKey,
    defaultModel: config.defaultModel ?? '',
    enabled: config.enabled,
    ...(config.protocol === undefined ? {} : { protocol: config.protocol }),
    ...(config.vendorId === undefined ? {} : { vendorId: config.vendorId }),
    ...(config.logo === undefined ? {} : { logo: config.logo }),
  };
}
